import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import {
  ExtractSkeleton,
  ScreenGreeting,
  TransactionListSkeleton,
} from "../../../Components/Molecules";
import { GridPagination } from "../../../Components/Molecules/GridHistory";
import {
  BAAS_STATEMENT_PAGE_SIZE,
  MAX_STATEMENT_ITEMS,
  useAccountBalanceData,
  useAccountData,
  useTransactionsData,
} from "../../../Hooks";
import { useFilter } from "../../../Hooks/useFilter";
import { HomeBalanceCards } from "../../../Components/Molecules/HomeBalanceCards";
import { fetchStatementCategories } from "../../../Services/statementCategoriesApi";
import {
  buildConsolidatedMonthRows,
  ExtractDailyConsolidated,
  ExtractFutureEntries,
  ExtractMonthlyConsolidated,
} from "./ExtractConsolidatedViews";
import { ExtractDayTimeline } from "./ExtractDayTimeline";
import { ExtractExportModal } from "./ExtractExportModal";
import { ExtractFilterBar } from "./ExtractFilterBar";
import { ExtractPeriodSummary } from "./ExtractPeriodSummary";
import { ExtractQuickActions } from "./ExtractQuickActions";
import { ExtractTimelineToggleAll } from "./ExtractTimelineToggleAll";
import {
  applyExtractClientFilters,
  extractFilterObjFromFilters,
  filtersFromExtractFilterObj,
  flattenTransactionDayGroups,
  paginateExtractDayGroups,
  paginateList,
  regroupTransactionsByDay,
} from "./extractFilters";
import {
  getStoredSelectedAccountId,
  IB_ACTIVE_ACCOUNT_CHANGED_EVENT,
  isLegacyAccountTypeId,
} from "../../../Constants/accountTypes";
import { useActiveIbAccount } from "../../../Hooks/useActiveIbAccount";
import {
  balancesFromMeAccount,
  mergeNormalizedAccountBalances,
  resolveMeAccount,
} from "../../../Hooks/Normalizers";
import { useAuth } from "../../../Contexts/AuthContext";
import { InternalTransferModal } from "../Transfers/InternalTransferModal";

const DEFAULT_DAYS_PER_PAGE = 10;
const EMPTY_SUB_ACCOUNTS = [];

const VIEWS_WITH_FILTER_BAR = new Set([
  "movimentacoes",
  "consolidado-diario",
  "consolidado-mensal",
  "lancamentos-futuros",
]);

const VIEWS_WITH_PAGINATION = new Set([
  "movimentacoes",
  "consolidado-diario",
  "consolidado-mensal",
]);

export const Extract = () => {
  const { hasPermission } = useAuth();
  const { account: activeIbAccount } = useActiveIbAccount();
  const canTransfer = hasPermission("transp2p");
  const [page, setPage] = useState(1);
  const [daysPerPage, setDaysPerPage] = useState(DEFAULT_DAYS_PER_PAGE);
  const [filters, setFilters] = useState(null);
  const [activeView, setActiveView] = useState("movimentacoes");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState(true);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const filterBarRef = useRef<HTMLDivElement>(null);

  const {
    handleGET: fetchTransactionsGET,
    loading,
    state: transactionsData,
  } = useTransactionsData();

  const { getFilterObj, handleRemoveFilters } = useFilter();
  const extractFilterObj = getFilterObj("extract") as Record<string, string>;
  const { handleGET: fetchAccountGET, state: accountData } = useAccountData();
  const {
    state: accountBalanceData,
    handleGET: fetchAccountBalanceGET,
    balanceValue,
    loading: balanceLoading,
    accountId: balanceAccountId,
  } = useAccountBalanceData();

  const extractAccountId = () =>
    activeIbAccount?.id || getStoredSelectedAccountId();

  const extractBalanceData = useMemo(() => {
    const selectedId = String(extractAccountId() || "").trim();
    const fromMe = balancesFromMeAccount(
      resolveMeAccount(accountData?.accounts, selectedId),
    );
    // Always merge GET + /me. Returning only fromMe when account_id was missing
    // dropped Over Balance (limitCents / showLimit) from GET /balances.
    return mergeNormalizedAccountBalances(accountBalanceData, fromMe)
      || accountBalanceData
      || fromMe;
  }, [activeIbAccount, accountData?.accounts, accountBalanceData, balanceAccountId]);

  const buildStatementQuery = useCallback(
    () => ({
      page: 1,
      // BaaS max page_size = 100; hook pages until done or max_items.
      page_size: BAAS_STATEMENT_PAGE_SIZE,
      fetch_all: true,
      max_items: MAX_STATEMENT_ITEMS,
      ...extractFilterObj,
    }),
    [extractFilterObj],
  );

  const extractApiFilterKey = [
    extractFilterObj.start_date,
    extractFilterObj.end_date,
    extractFilterObj.start_amount,
    extractFilterObj.end_amount,
    extractFilterObj.sub_account_id,
  ] as const;

  const refreshExtractForActiveAccount = useCallback(() => {
    fetchAccountGET();
    fetchAccountBalanceGET({ account_id: extractAccountId() });
    fetchTransactionsGET(buildStatementQuery());
    // handleGET from useCallAxios is not memoized; keep this callback stable on filter changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, extractApiFilterKey);

  useEffect(() => {
    setFilters((prev) => {
      const fromApi = filtersFromExtractFilterObj(extractFilterObj);
      return {
        ...fromApi,
        types: prev?.types ?? [],
        categories: prev?.categories ?? [],
        searchQuery: prev?.searchQuery ?? "",
      };
    });
  }, extractApiFilterKey);

  useEffect(() => {
    refreshExtractForActiveAccount();
    // Same as refreshExtractForActiveAccount: do not depend on unstable handleGET identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, extractApiFilterKey);

  useEffect(() => {
    const onActiveAccountChanged = () => {
      refreshExtractForActiveAccount();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    return () => {
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    };
  }, [refreshExtractForActiveAccount]);

  useEffect(() => {
    const accounts = accountData?.accounts;
    if (!Array.isArray(accounts) || accounts.length === 0) return;
    const selectedId = String(extractAccountId() || "").trim();
    const matched = resolveMeAccount(accounts, selectedId);
    const id = String(matched?.id || matched?.account_id || "").trim();
    if (!id || isLegacyAccountTypeId(id)) return;
    fetchAccountBalanceGET({ account_id: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData?.accounts, activeIbAccount?.id]);

  useEffect(() => {
    let cancelled = false;
    setCategoriesLoading(true);
    fetchStatementCategories()
      .then((items) => {
        if (!cancelled) setCategoryOptions(items);
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Reload catalog when account/API date filters change (active account may switch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, extractApiFilterKey);

  const flatItems = useMemo(
    () => flattenTransactionDayGroups(transactionsData?.items),
    [transactionsData?.items],
  );

  const filteredFlatItems = useMemo(
    () => applyExtractClientFilters(flatItems, filters),
    [flatItems, filters],
  );

  const displayGroups = useMemo(
    () => regroupTransactionsByDay(filteredFlatItems),
    [filteredFlatItems],
  );

  const paginatedDayGroups = useMemo(
    () => paginateExtractDayGroups(displayGroups, page, daysPerPage),
    [displayGroups, page, daysPerPage],
  );

  const timelineDayKey = useMemo(
    () =>
      paginatedDayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [paginatedDayGroups],
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [timelineDayKey, page]);

  const monthRows = useMemo(
    () => buildConsolidatedMonthRows(displayGroups),
    [displayGroups],
  );

  const paginatedMonthRows = useMemo(
    () => paginateList(monthRows, page, daysPerPage),
    [monthRows, page, daysPerPage],
  );

  const totalDayGroups = displayGroups.length;
  const paginationTotal =
    activeView === "consolidado-mensal" ? monthRows.length : totalDayGroups;
  const currentPageCount =
    activeView === "consolidado-mensal"
      ? paginatedMonthRows.length
      : paginatedDayGroups.length;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(paginationTotal / daysPerPage) || 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [paginationTotal, daysPerPage, page]);

  useEffect(() => {
    setPage(1);
  }, [activeView]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    handleRemoveFilters("extract", extractFilterObjFromFilters(nextFilters));
  };

  const handleDaysPerPageChange = (value) => {
    setDaysPerPage(value);
    setPage(1);
  };

  const handleOpenFilters = () => {
    if (!VIEWS_WITH_FILTER_BAR.has(activeView)) {
      setActiveView("movimentacoes");
    }
    requestAnimationFrame(() => {
      filterBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleOpenExport = () => {
    setExportModalOpen(true);
  };

  const hasItems = (transactionsData?.items?.length ?? 0) > 0;
  const isInitialLoading = loading.GET && !hasItems;

  if (isInitialLoading || filters === null) {
    return (
      <div className="extract-page min-w-0 w-full max-w-full overflow-x-hidden">
        <ScreenGreeting title={intl.get(`phrases.myExtract`)} />
        <ExtractSkeleton />
      </div>
    );
  }

  const showPagination = VIEWS_WITH_PAGINATION.has(activeView);
  const showFilterBar = VIEWS_WITH_FILTER_BAR.has(activeView);
  const paginationItemLabel =
    activeView === "consolidado-mensal"
      ? intl.get("common.months")
      : intl.get("common.days");

  return (
    <div className="extract-page min-w-0 w-full max-w-full overflow-x-hidden">
      <ScreenGreeting title={intl.get(`phrases.myExtract`)} />

      <HomeBalanceCards
        key={activeIbAccount?.id || "ib-extract-balances"}
        accountBalanceData={extractBalanceData}
        balanceValue={balanceValue}
        loadingRefresh={balanceLoading}
        onRefresh={() =>
          fetchAccountBalanceGET({
            account_id: extractAccountId(),
          })
        }
      />

      <ExtractQuickActions
        onExportClick={handleOpenExport}
        onOpenFilters={handleOpenFilters}
        onConsolidadoDiario={() => setActiveView("consolidado-diario")}
        onOpenTransfer={canTransfer ? () => setTransferModalOpen(true) : undefined}
      />

      <div ref={filterBarRef} className="mb-4 min-w-0 w-full max-w-full overflow-x-hidden">
        <ExtractFilterBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filteredItems={filteredFlatItems}
          subAccounts={EMPTY_SUB_ACCOUNTS}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
          onExportClick={handleOpenExport}
          activeTab={activeView}
          onTabChange={setActiveView}
          showFilters={showFilterBar}
        />
        {showFilterBar && (
          <ExtractPeriodSummary items={filteredFlatItems} className="mt-4" />
        )}
      </div>

      <div
        className={
          showFilterBar
            ? "min-w-0 w-full max-w-full overflow-x-hidden"
            : "app-card min-w-0 w-full max-w-full overflow-hidden"
        }
      >
        {loading.GET ? (
          <TransactionListSkeleton rows={12} withBalanceRows />
        ) : (
          <>
            {activeView === "movimentacoes" && (
              <>
                {paginatedDayGroups.length > 0 && (
                  <ExtractTimelineToggleAll
                    allExpanded={allExpanded}
                    onToggle={() => setAllExpanded((prev) => !prev)}
                  />
                )}
                <ExtractDayTimeline
                  dayGroups={paginatedDayGroups}
                  allExpanded={allExpanded}
                  onAllExpandedChange={setAllExpanded}
                  onRefundSuccess={refreshExtractForActiveAccount}
                />
              </>
            )}

            {activeView === "consolidado-diario" && (
              <ExtractDailyConsolidated dayGroups={paginatedDayGroups} />
            )}

            {activeView === "consolidado-mensal" && (
              <ExtractMonthlyConsolidated rows={paginatedMonthRows} />
            )}

            {activeView === "lancamentos-futuros" && <ExtractFutureEntries />}

            {showPagination && (
              <div className="mt-4">
                <GridPagination
                  total={paginationTotal}
                  page={page}
                  rowsPerPage={daysPerPage}
                  currentPageCount={currentPageCount}
                  onPageChange={setPage}
                  onRowsPerPageChange={handleDaysPerPageChange}
                  itemLabel={paginationItemLabel}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ExtractExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        items={filteredFlatItems}
      />

      {canTransfer ? (
        <InternalTransferModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
        />
      ) : null}
    </div>
  );
};
